const HANDOFF_QUERY_KEY = 'leadHandoffToken'
const HANDOFF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,255}$/
const HANDOFF_STORAGE_KEY = 'miinta_inbound_lead_handoff'
const HANDOFF_STORAGE_VERSION = 1
const HANDOFF_TTL_MS = 30 * 60 * 1000

interface PendingInboundLeadHandoff {
  capturedAt: number
  token: string
  version: number
}

let pendingHandoff: PendingInboundLeadHandoff | null = null

export const normalizeInboundLeadHandoffToken = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return HANDOFF_TOKEN_PATTERN.test(normalized) ? normalized : null
}

const removeStoredHandoff = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(HANDOFF_STORAGE_KEY)
  } catch {
    // The in-memory handoff still supports browsers that disable storage.
  }
}

const isFreshHandoff = (handoff: PendingInboundLeadHandoff): boolean => {
  const age = Date.now() - handoff.capturedAt
  return age >= 0 && age < HANDOFF_TTL_MS
}

const persistHandoff = (handoff: PendingInboundLeadHandoff): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(handoff))
  } catch {
    // Auth0 appState and in-memory state remain available as fallbacks.
  }
}

const restoreStoredHandoff = (): PendingInboundLeadHandoff | null => {
  if (typeof window === 'undefined') return null
  try {
    const serialized = window.sessionStorage.getItem(HANDOFF_STORAGE_KEY)
    if (!serialized) return null
    const candidate = JSON.parse(serialized) as Partial<PendingInboundLeadHandoff>
    const token = normalizeInboundLeadHandoffToken(candidate.token)
    if (
      candidate.version !== HANDOFF_STORAGE_VERSION ||
      typeof candidate.capturedAt !== 'number' ||
      !Number.isFinite(candidate.capturedAt) ||
      !token
    ) {
      removeStoredHandoff()
      return null
    }
    const handoff = { capturedAt: candidate.capturedAt, token, version: HANDOFF_STORAGE_VERSION }
    if (!isFreshHandoff(handoff)) {
      removeStoredHandoff()
      return null
    }
    return handoff
  } catch {
    removeStoredHandoff()
    return null
  }
}

export const captureInboundLeadHandoffToken = (value: unknown): string | null => {
  const normalized = normalizeInboundLeadHandoffToken(value)
  if (normalized) {
    pendingHandoff = {
      capturedAt: Date.now(),
      token: normalized,
      version: HANDOFF_STORAGE_VERSION
    }
    persistHandoff(pendingHandoff)
  }
  return normalized
}

export const clearInboundLeadHandoffToken = (): void => {
  pendingHandoff = null
  removeStoredHandoff()
}

export const captureInboundLeadHandoffTokenFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null

  const currentUrl = new URL(window.location.href)
  if (!currentUrl.searchParams.has(HANDOFF_QUERY_KEY)) return null

  const token = captureInboundLeadHandoffToken(currentUrl.searchParams.get(HANDOFF_QUERY_KEY))
  if (!token) clearInboundLeadHandoffToken()
  currentUrl.searchParams.delete(HANDOFF_QUERY_KEY)
  window.history.replaceState(
    window.history.state,
    '',
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
  )
  return token
}

export const peekInboundLeadHandoffToken = (): string | null => {
  if (pendingHandoff && isFreshHandoff(pendingHandoff)) return pendingHandoff.token
  pendingHandoff = restoreStoredHandoff()
  return pendingHandoff?.token ?? null
}
